import { RawLink } from '@outdoorsyco/bonfire';
import cn from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';

import OutdoorsyLogo from '@/assets/images/outdoorsy-logo.svg';
import { useBreakpointContext } from '@/components/common/BreakpointContext';
import { useRouter } from '@/hooks/useRouter';
import { trackNavClickedEvent } from '@/services/analytics/header';
import { get2FASessionCookie, reset2FASessionCookie } from '@/utility/session';

import { GlobalHeaderBecomeHostButton } from './GlobalHeaderBecomeHostButton';
import { GlobalHeaderMenu } from './GlobalHeaderMenu';
import { HeaderCenter } from './HeaderCenter';
import { ILegacyHeaderProps, legacyPropsToHeaderConfig } from './headerConfig';
import { HeaderFrame } from './HeaderFrame';

export const GlobalHeader = (props: ILegacyHeaderProps) => {
  const router = useRouter();

  const config = legacyPropsToHeaderConfig(props);
  const { center, surface, sticky } = config;

  const isTransparent = surface === 'transparent' || surface === 'transparent-dark';
  const hasSearchCenter = center.type === 'search';

  const { isAboveTablet } = useBreakpointContext();

  const [stuckToTop, setStuckToTop] = useState(false);

  // The 2FA lockdown is a security behavior: it is derived from the session
  // cookie here and is deliberately not part of the page-declared config.
  const requires2FA = useMemo(() => {
    if (router.pathname !== '/security') return false;
    const twoFASessionCookie = get2FASessionCookie();
    return !!twoFASessionCookie && !!twoFASessionCookie.show_2fa_screen;
  }, [router.pathname]);

  useEffect(() => {
    if (!sticky) return setStuckToTop(false);

    const updateStuckToTop = () => setStuckToTop(window.scrollY > 0);

    window.addEventListener('scroll', updateStuckToTop, { passive: true });

    updateStuckToTop();

    return () => {
      window.removeEventListener('scroll', updateStuckToTop);
    };
  }, [sticky]);

  const handleClickLogo = () => {
    trackNavClickedEvent({
      clickedLink: 'Outdoorsy',
      isInDropdown: false,
    });

    if (requires2FA) {
      reset2FASessionCookie();
    }
  };

  return (
    <HeaderFrame config={config} stuckToTop={stuckToTop}>
      <RawLink
        href="/"
        onClick={handleClickLogo}
        className={cn('mr-4 justify-self-start', {
          'hidden xl:block': hasSearchCenter,
        })}>
        <OutdoorsyLogo
          className={cn('translate-y-1 transform', {
            'text-white': surface === 'transparent' && !stuckToTop,
            'text-gray-900': !isTransparent || stuckToTop,
          })}
        />
      </RawLink>

      {!requires2FA && (
        <>
          <HeaderCenter center={center} />

          <div className="flex items-center justify-self-end gap-4 whitespace-nowrap">
            <div className="hidden lg:block">
              <GlobalHeaderBecomeHostButton />
            </div>

            <GlobalHeaderMenu showBurgerMenuIcon={Boolean(isAboveTablet || !hasSearchCenter)} />
          </div>
        </>
      )}
    </HeaderFrame>
  );
};
